/* eslint-disable react-hooks/exhaustive-deps */
// BUSCA POR IA — lógica compartilhada entre os heros que têm o campo de frase livre:
//   • modelo_busca = 4  (WelcomeIa: hero só de IA)
//   • modelo_busca = 10 (WelcomePadraoIa: hero Padrão + barra de IA)
// Frase → buscaIa (site-graphql) → URL REAL da /imoveis (mesmo formato do renderPath dos
// WelcomePieces: /imoveis/<transacao>/<cidade>/<bairro>/<empreendimento>/<tipo>?min=&max=...)
// → navega. NUNCA trava: erro de rede/IA degrada pra busca normal (cidade por substring ou
// /imoveis geral). Extraído do WelcomeIa pra não existirem duas cópias da mesma régua.
import { hifenUrl } from '../../../utils/hifenUrl';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    buscaIa,
    BuscaIaDisambiguation,
    BuscaIaFilters,
    cidadeDoBairro,
    parseBuscaIaDesambiguacao,
    parseBuscaIaFilters,
} from '../../../services/aiSearch';
import { district } from '../../../services/selectors';
import {
    acrescentarQuery,
    lerPontoReferencia,
    montarCaminhoBuscaIa,
    normalizarOutrasCidades,
    ORDEM_DISTANCIA,
    PontoBuscaIa,
    queryBuscaIa,
    salvarFaixaBuscaIa,
} from '../../../utils/buscaIa';
import { removerAcentos, segmentoFiltroSeguro } from '../../../utils/FunctionsGlobals';
import { appendCodsiteQuery } from '../../../utils/url';
import { useRegiaoHref } from '../../context/Info';

// rótulos das transações no toggle do hero (mesmo vocabulário do TopoCentral/WelcomeEditorial)
export const TRANSACAO_LABEL: Record<string, string> = {
    venda: 'Comprar',
    aluguel: 'Alugar',
    temporada: 'Temporada',
};

// A frase só sobrepõe o toggle quando o visitante DIZ a transação. Sem menção, a IA devolve
// venda=1 por padrão — e esse default não pode apagar o botão que a pessoa acabou de clicar.
const TRANSACAO_NA_FRASE: Array<{ v: string; re: RegExp }> = [
    { v: 'temporada', re: /temporad|di[áa]ri|f[ée]ria|veraneio|fim de semana/i },
    { v: 'aluguel', re: /alug|loca[çc]|arrend/i },
    { v: 'venda', re: /compr|vend|[àa] venda|financ/i },
];

export const transacaoDitaNaFrase = (texto: string): string | null =>
    TRANSACAO_NA_FRASE.find((t) => t.re.test(removerAcentos(String(texto || ''))))?.v ?? null;

const toPositiveInt = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const toIdArray = (value: unknown): number[] => {
    if (!Array.isArray(value)) {
        const single = toPositiveInt(value);
        return single ? [single] : [];
    }
    return value.map(toPositiveInt).filter((v) => v > 0);
};

// codificação de segmento idêntica à da lista /imoveis ('-' vira 002D ANTES de espaço→'-').
// Usada só para o CÓDIGO (referência): a listagem busca o código pelo texto literal, então ele
// não pode virar slug. Cidade/bairro/tipo saem por `segmentoFiltroSeguro` (utils/FunctionsGlobals).
export const encodeSegment = (label: unknown, codsite?: unknown): string => {
    const raw = String(label ?? '').trim();
    if (!raw) return '';
    return removerAcentos(
        hifenUrl(raw, codsite).replaceAll('\n', '-').replaceAll(' ', '-')
    )
        .toLowerCase()
        .replaceAll(' ', '');
};

const normalizeForMatch = (value: unknown): string =>
    removerAcentos(String(value ?? ''))
        .toLowerCase()
        .trim();

export interface UseBuscaIaParams {
    allCities: Array<{ label: string; value: any; id_estado?: any; imoveis?: any }>;
    allTypes: Array<{ label: string; value: any }>;
    allTransacao: Array<{ label: string; value: string }>;
    site: any;
    origin?: any;
    codsite?: any; // codsite p/ headers das queries (mesma convenção dos WelcomePieces)
    codsite2?: any; // codsite da query string (?codsite=) p/ ambientes de teste
    valores?: any; // {precos_venda: [{min,max}], precos_locacao: [{min,max}]}
    /**
     * Transação escolhida FORA do hook (o hero Padrão+IA usa o select clássico de
     * Transação). Quando informada, vale como escolha explícita do visitante: só a
     * frase ("alugar"/"comprar") sobrepõe — nunca o default venda=1 da IA.
     */
    transacaoExterna?: string | null;
    /** Categoria fixada no toggle do hero (opção "Lançamentos" do cs2619): entra como
     *  `?categoria=<id>` em TODA URL que a busca por IA gerar. null = comportamento de sempre. */
    categoriaFixa?: number | null;
}

export function useBuscaIa({
    allCities,
    allTypes,
    allTransacao,
    site,
    origin,
    codsite,
    codsite2,
    valores,
    transacaoExterna,
    categoriaFixa,
}: UseBuscaIaParams) {
    const router = useRouter();
    // MULTI-FILIAIS: preserva a filial ativa no link de resultado da busca por IA.
    const withRegiao = useRegiaoHref();
    const [texto, setTexto] = useState('');
    const [loading, setLoading] = useState(false);
    const [mensagem, setMensagem] = useState('');
    const [desambiguacao, setDesambiguacao] = useState<BuscaIaDisambiguation | null>(null);
    const mountedRef = useRef(true);
    const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
        };
    }, []);

    const defaultTransacao = useMemo(() => {
        const first = String(allTransacao?.[0]?.value || site?.transacoes?.[0] || 'venda').toLowerCase();
        return ['venda', 'aluguel', 'temporada'].includes(first) ? first : 'venda';
    }, [allTransacao, site?.transacoes]);

    const tenantHasAluguel = useMemo(
        () => (allTransacao || []).some((t) => String(t?.value || '').toLowerCase() === 'aluguel'),
        [allTransacao]
    );
    // Opções do toggle = as transações CONFIGURADAS no tenant (site.transacoes →
    // TransacaoPriorizada), na MESMA ordem e com os mesmos rótulos dos demais heros.
    // Lista fixa venda/aluguel escondia Temporada de quem a tem ligada (cs2619/cs1731).
    const opcoesTransacao = useMemo(() => {
        const vistos = new Set<string>();
        const opcoes: Array<{ v: string; l: string }> = [];
        for (const t of allTransacao || []) {
            const v = String(t?.value || '').toLowerCase();
            const l = TRANSACAO_LABEL[v];
            if (!l || vistos.has(v)) continue;
            vistos.add(v);
            opcoes.push({ v, l });
        }
        return opcoes;
    }, [allTransacao]);
    // Transação SEMPRE definida (a URL da lista depende dela). Com mais de uma transação
    // configurada, mostra o toggle; depois de clicado, só a frase ("alugar"/"comprar") sobrepõe.
    const [transacaoInterna, setTransacaoInterna] = useState<string>(defaultTransacao);
    // escolha EXPLÍCITA no toggle (clique) — o default do tenant não conta
    const [transacaoTocadaInterna, setTransacaoTocadaInterna] = useState(false);

    // Com transação externa (select do hero Padrão), ela manda e conta como "tocada".
    const externaNormalizada = transacaoExterna ? String(transacaoExterna).toLowerCase() : '';
    const transacaoSel = externaNormalizada || transacaoInterna;
    const transacaoTocada = externaNormalizada ? true : transacaoTocadaInterna;

    const escolherTransacao = (v: string) => {
        setTransacaoInterna(v);
        setTransacaoTocadaInterna(true);
    };

    // exemplos usam as cidades com MAIS imóveis do tenant (a lista chega em ordem
    // alfabética/priorizada — sem ordenar, "Atibaia" com 2 imóveis viraria o exemplo)
    const topCities = useMemo(
        () => [...(allCities || [])].sort((a: any, b: any) => (Number(b?.imoveis) || 0) - (Number(a?.imoveis) || 0)),
        [allCities]
    );
    const placeholderCity = topCities?.[0]?.label || 'sua cidade';
    const placeholder = `apartamento 2 quartos em ${placeholderCity} até R$ 600 mil`;

    // (26/08, Daniel) Os 3 chips de exemplo abaixo da busca ("Apartamento 2 quartos em X",
    // "Casa com 3 dormitórios em X", "Imóvel para alugar em X") foram REMOVIDOS dos heros 4 e 10.
    // O placeholder do campo continua dando o exemplo de frase.

    // MULTI-FILIAIS: a busca por IA leva a filial ativa para a listagem (?regiao=).
    /** `?categoria=<id>` da opção "Lançamentos": carimbado aqui, no ÚLTIMO passo, pra valer em
     *  todos os destinos da IA (frase, fallback por cidade e busca por código) sem repetir a
     *  regra em cada um. Sem categoria fixa a URL sai idêntica à de antes. */
    const comCategoriaFixa = (path: string) => {
        if (categoriaFixa == null) return path;
        if (/[?&]categoria=/.test(path)) return path;
        return `${path}${path.includes('?') ? '&' : '?'}categoria=${categoriaFixa}`;
    };

    const finalizeUrl = (path: string) =>
        withRegiao(
            removerAcentos(appendCodsiteQuery(comCategoriaFixa(path), codsite2, origin)).toLowerCase().replaceAll(' ', '-')
        );

    // monta a URL de /imoveis que a listagem REALMENTE entende (ver pages/imoveis/index.tsx):
    // path /imoveis/<model>/<city>/<district>/<company>/<type> + query min/max/area + os
    // parâmetros da busca por IA (utils/buscaIa: ia=1, *_min, perto…).
    // `ponto` = centro do "perto de…" (v2): entra como ?perto=&perto_rotulo=&ordem=asc-distancia.
    const buildImoveisUrl = async (
        filters: BuscaIaFilters,
        textoBusca?: string,
        ponto?: (PontoBuscaIa & { rotulo?: string }) | null
    ): Promise<{ url: string; vazio: boolean; ia: boolean }> => {
        const daIa =
            Number(filters?.venda) === 1
                ? 'venda'
                : Number(filters?.aluguel) === 1
                ? 'aluguel'
                : Number(filters?.temporada) === 1
                ? 'temporada'
                : null;
        // Quem escolheu no toggle manda, a menos que a frase DIGA outra transação: a IA devolve
        // venda=1 por padrão em frase sem transação ("apartamento 2 quartos") e isso jogava quem
        // clicou em Temporada/Alugar direto pra /imoveis/venda.
        const ditaNaFrase = transacaoDitaNaFrase(String(textoBusca ?? texto));
        // só vale a transação que o tenant realmente tem (senão a frase manda pra uma lista vazia)
        const daFrase = opcoesTransacao.some((o) => o.v === ditaNaFrase) ? ditaNaFrase : null;
        const transacao = (transacaoTocada ? daFrase || transacaoSel : daIa) || transacaoSel;

        // Referência/código: vai pra rota [code] (7º segmento). A lista busca por
        // tbl_imovel.Referencia e IGNORA preço/transação — é a busca por código já pronta.
        const referencias = Array.isArray(filters?.referencia)
            ? filters.referencia.map((r) => String(r ?? '').trim()).filter(Boolean)
            : [];
        if (referencias.length) {
            const code = encodeSegment(referencias[0], site?.id);
            return { url: finalizeUrl(`/imoveis/${transacao}/-/-/-/-/${code}`), vazio: false, ia: false };
        }

        // Bairro SEM cidade: a listagem só casa bairro dentro de uma cidade, então descobre a
        // cidade pelo próprio bairro (a v2 já manda id_cidade junto; isto é a rede de segurança).
        const idBairros = toIdArray(filters?.id_bairro);
        let idCidade = toPositiveInt(filters?.id_cidade);
        if (!idCidade && idBairros.length > 0) {
            idCidade = await cidadeDoBairro(idBairros[0], origin, codsite);
        }
        const cidadeMatch = idCidade
            ? (allCities || []).find((c) => Number(c?.value) === idCidade)
            : null;

        // bairros vêm como ids → resolve labels via bairros(id_cidade) (mesma fonte da lista)
        let bairros: string[] = [];
        if (cidadeMatch && idBairros.length > 0) {
            try {
                const infoDistrict: any = await district(idCidade, origin, codsite);
                bairros = (infoDistrict?.bairros || [])
                    .filter((b: any) => idBairros.includes(toPositiveInt(b?.id)))
                    .map((b: any) => String(b?.nome ?? ''));
            } catch {
                bairros = [];
            }
        }

        const idTipos = toIdArray(filters?.id_tipo);
        const tipos = idTipos.length
            ? (allTypes || []).filter((t) => idTipos.includes(toPositiveInt(t?.value))).map((t) => String(t?.label ?? ''))
            : [];

        // preço: a lista aplica min/max NA transação ativa e valida contra os bounds do tenant
        const limitesPreco =
            transacao === 'aluguel' || transacao === 'temporada'
                ? valores?.precos_locacao?.[0]
                : valores?.precos_venda?.[0];

        // Quartos/suítes/banheiros/vagas vão como MÍNIMO (`*_min`): "garden com 2 suítes" = 2 ou
        // mais. Os `dormitorios=`/`suites=` de antes eram EXATOS e zeravam a busca.
        const { caminho, vazio, minimos } = montarCaminhoBuscaIa({
            filters,
            transacao,
            cidade: cidadeMatch,
            bairros,
            tipos,
            limitesPreco,
            ordemDistancia: !!ponto,
        });

        // Parâmetros da IA DEPOIS do finalizeUrl (que passa a URL por toLowerCase/sem acento):
        // assim o `perto_rotulo` chega à listagem como a IA escreveu ("Paraíso").
        const sufixo = [
            queryBuscaIa({ ia: true, minimos, perto: ponto || null, pertoRotulo: ponto?.rotulo }),
            ponto ? `ordem=${ORDEM_DISTANCIA}` : '',
        ]
            .filter(Boolean)
            .join('&');
        return { url: acrescentarQuery(finalizeUrl(caminho), sufixo), vazio, ia: true };
    };

    // degradação: tenta casar o texto com uma cidade por substring; senão /imoveis geral
    const buildFallbackUrl = (): string => {
        const normalizedText = normalizeForMatch(texto);
        const cityMatch = normalizedText
            ? (allCities || []).find((c) => {
                  const label = normalizeForMatch(c?.label);
                  return label.length > 2 && normalizedText.includes(label);
              })
            : null;
        const citySegment = cityMatch ? segmentoFiltroSeguro(cityMatch.label, site?.id) : '';
        const path = citySegment
            ? `/imoveis/${transacaoSel}/${citySegment}/-/-/-`
            : `/imoveis/${transacaoSel}/-/-/-/-?ordem=desc-inclusao`;
        return finalizeUrl(path);
    };

    const navigate = (url: string, message: string, delayMs: number) => {
        if (!mountedRef.current) return;
        if (message) setMensagem(message);
        if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
        navigateTimerRef.current = setTimeout(() => {
            router.push(url).catch(() => {
                if (mountedRef.current) setLoading(false);
            });
        }, message ? delayMs : 0);
    };

    // Busca DIRETA por código (referência): rota [code], sem passar pela IA.
    const buscarPorCodigo = (codigoRaw: string) => {
        const code = String(codigoRaw ?? '').trim();
        if (!code || loading) return;
        setDesambiguacao(null);
        setLoading(true);
        navigate(
            finalizeUrl(`/imoveis/${transacaoSel}/-/-/-/-/${encodeSegment(code, site?.id)}`),
            'Buscando o imóvel...',
            300
        );
    };

    const submit = async (rawText?: string) => {
        const finalText = String(rawText ?? texto).trim();
        if (loading) return;
        // Sem frase o botão ficava DESABILITADO: quem clicava numa transação e mandava buscar
        // não ia a lugar nenhum. Sem texto a busca é a própria transação escolhida (a lista
        // já abre com os filtros na lateral).
        if (!finalText) {
            setLoading(true);
            navigate(buildFallbackUrl(), 'Buscando imóveis...', 300);
            return;
        }
        if (rawText !== undefined) setTexto(finalText);

        setLoading(true);
        setMensagem('Entendendo sua busca...');

        try {
            const result = await buscaIa(finalText, origin, codsite);
            if (!mountedRef.current) return;

            if (!result) {
                navigate(buildFallbackUrl(), 'Buscando imóveis...', 600);
                return;
            }

            // Desambiguação: número que é código E valor plausível → pergunta em vez
            // de navegar. Renderiza os botões e espera o clique do visitante.
            const desamb = parseBuscaIaDesambiguacao(result?.desambiguacao);
            if (desamb) {
                setLoading(false);
                setMensagem(desamb.pergunta);
                setDesambiguacao(desamb);
                return;
            }
            setDesambiguacao(null);

            const filters = parseBuscaIaFilters(result?.filters);
            const ponto = lerPontoReferencia(result?.ponto_referencia);
            // "Centro de outra cidade?": a MESMA busca naquela cidade/bairro, montada pelo mesmo
            // buildImoveisUrl (sem o ponto — ele é da cidade escolhida). Em paralelo com a
            // principal; a que falhar só não vira link.
            // Só cidade que a listagem conhece (allCities): fora dela o link sairia sem cidade.
            const outrasCidades = normalizarOutrasCidades(result?.outras_cidades).filter((oc) =>
                (allCities || []).some((c) => Number(c?.value) === oc.id_cidade)
            );
            const [principal, ...outras] = await Promise.all([
                buildImoveisUrl(filters, finalText, ponto),
                ...outrasCidades.map((oc) =>
                    buildImoveisUrl({ ...filters, id_cidade: oc.id_cidade, id_bairro: oc.id_bairro }, finalText, null)
                        .then((r) => ({ nome: oc.nome, n: oc.n, url: r.url }))
                        .catch(() => null)
                ),
            ]);
            const { url, vazio } = principal;
            if (!mountedRef.current) return;

            const interpretacao = String(result?.interpretacao || '').trim();
            // A listagem mostra a faixa (aviso + "Também em: …") lendo isto pela URL gerada.
            if (principal.ia) {
                salvarFaixaBuscaIa(url, {
                    aviso: String(result?.aviso || ''),
                    interpretacao,
                    lugar: String(result?.lugar?.rotulo || ''),
                    outras_cidades: outras.filter((o): o is { nome: string; n: number; url: string } => !!o),
                    termos_ignorados: Array.isArray(result?.termos_ignorados) ? result.termos_ignorados : [],
                });
            }

            const message = vazio
                ? 'Não consegui identificar filtros na frase — mostrando todos os imóveis.'
                : interpretacao || 'Buscando imóveis...';
            navigate(url, message, vazio ? 1400 : 900);
        } catch {
            if (!mountedRef.current) return;
            navigate(buildFallbackUrl(), 'Buscando imóveis...', 600);
        }
    };

    const escolherDesambiguacao = async (opcao: { label: string; filters: BuscaIaFilters }) => {
        setDesambiguacao(null);
        setLoading(true);
        setMensagem('Buscando imóveis...');
        try {
            const { url, ia } = await buildImoveisUrl(opcao.filters);
            if (!mountedRef.current) return;
            // opção escolhida não tem aviso: apaga o de uma busca anterior com a mesma chave
            if (ia) salvarFaixaBuscaIa(url, null);
            navigate(url, 'Buscando imóveis...', 300);
        } catch {
            navigate(buildFallbackUrl(), 'Buscando imóveis...', 300);
        }
    };

    const limparDesambiguacao = () => setDesambiguacao(null);

    return {
        texto,
        setTexto,
        loading,
        mensagem,
        desambiguacao,
        limparDesambiguacao,
        transacaoSel,
        transacaoTocada,
        escolherTransacao,
        opcoesTransacao,
        defaultTransacao,
        tenantHasAluguel,
        placeholder,
        submit,
        escolherDesambiguacao,
        buscarPorCodigo,
    };
}

// Ícone de "faísca" da IA — compartilhado pelos heros que têm o campo de frase livre.
export const SparkIcon = ({ size = 18, className }: { size?: number; className?: string }) => (
    <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="currentColor"
        aria-hidden="true"
    >
        <path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2z" />
        <path d="M19 14l0.9 2.6L22.5 17.5l-2.6 0.9L19 21l-0.9-2.6L15.5 17.5l2.6-0.9L19 14z" opacity="0.8" />
        <path d="M5.5 15l0.7 2L8.2 17.7l-2 0.7L5.5 20.4l-0.7-2L2.8 17.7l2-0.7L5.5 15z" opacity="0.6" />
    </svg>
);
